import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../generic-components/Heading';
import Button from '../../../../generic-components/Button';
import { ICategory, ICategoryDetails, ITransaction } from '../../../../types';
import {
  canUserCategorize,
  isCategorized,
  isCategorizedAsBusiness,
  isCategorizedOnlyAsPersonal,
  getCategoryIdFromTransaction,
} from '../../../transactions/utils/categorization';
import { CHOOSE_CATEGORY_STYLE } from './style';
import { ReactComponent as DownArrowIconTransparent } from '../../../../assets/svg/down-arrow-icon-transparent.svg';
import { CategorizeContext } from '../../CategorizeContainer';

interface IChooseCategoryProps {
  transactionsCategories: ICategory[];
  transaction: ITransaction;
  onCategorySelection: () => void;
  selectedCategoryId?: string;
  isReadOnly?: boolean;
}

type IBadgeState = {
  [key: string]: string
}

const BADGE_STATES: IBadgeState = {
  PRE_SELECTED: 'PRE_SELECTED',
  UNSELECTED: 'UNSELECTED',
  IS_BUSINESS: 'IS_BUSINESS',
  READ_ONLY: 'READ_ONLY',
};

const getChooseCategoryState = (canCategorizeTransaction: boolean, isTransactionCategorized: boolean, isTransactionCategoryPreSelected: string, categoryDetails: ICategoryDetails, isReadOnly: boolean | undefined) => {
  if (isReadOnly) {
    return BADGE_STATES.READ_ONLY;
  }
  if (!isTransactionCategorized) {
    if (canCategorizeTransaction && isTransactionCategoryPreSelected) return BADGE_STATES.PRE_SELECTED;
    if (canCategorizeTransaction && !isTransactionCategoryPreSelected) return BADGE_STATES.UNSELECTED;
  } else {
    if (isCategorizedOnlyAsPersonal(categoryDetails)) return BADGE_STATES.PRE_SELECTED;
    if (isCategorizedAsBusiness(categoryDetails)) return BADGE_STATES.IS_BUSINESS;
  }
  return BADGE_STATES.UNSELECTED;
};

const getBadgeStyle = (stylesObject: any, badgeState: string) => stylesObject[badgeState];

export const ChooseCategory = (
  {
    transactionsCategories,
    transaction,
    onCategorySelection,
    selectedCategoryId,
    isReadOnly,
  } :
    IChooseCategoryProps,
) => {
  const context = useContext(CategorizeContext);
  const { t } = useTranslation();
  const isTransactionCategorized = isCategorized(transaction?.categoryDetails);
  const canCategorizeTransaction = canUserCategorize(transaction?.allowCategorizationEnum || '', transaction?.categorizationType || '');
  const selectedOrSuggestedCatId = selectedCategoryId || getCategoryIdFromTransaction(transaction);
  const chooseCategoryState = getChooseCategoryState(canCategorizeTransaction, isTransactionCategorized, transaction?.suggestedTaxCategoryId, transaction?.categoryDetails, (context?.isReadOnlyMode || isReadOnly));
  const getCategoryTitle = (catId: string | null, isMarkAsPersonal: boolean) => {
    if (isMarkAsPersonal) return t('CATEGORIZE_EXPENSES_COMPONENT_CATEGORY_LABEL_PERSONAL');
    if (!catId) return t('CATEGORIZE_EXPENSES_COMPONENT_CATEGORY_LABEL');
    const category = transactionsCategories?.filter(c => c.id === catId)[0];
    if (category) {
      return category.title || t('CATEGORIZE_EXPENSES_COMPONENT_CATEGORY_LABEL');
    }
    return t('CATEGORIZE_EXPENSES_COMPONENT_CATEGORY_LABEL');
  };
  if (!canCategorizeTransaction && !isTransactionCategorized) return null;
  return (
    <Button
      onClick={canCategorizeTransaction ? onCategorySelection : () => ({})}
      className={`${CHOOSE_CATEGORY_STYLE.WRAPPERS.SHARED} ${getBadgeStyle(CHOOSE_CATEGORY_STYLE.WRAPPERS, chooseCategoryState)}`}
      title=''
      disable={context.isReadOnlyMode || isReadOnly}
      id='category-selector'
      childElement={(
        <>
          <Heading
            id='selectedCategoryTitle'
            title={getCategoryTitle(selectedOrSuggestedCatId ?? null, isCategorizedOnlyAsPersonal(transaction?.categoryDetails))}
            className={`${CHOOSE_CATEGORY_STYLE.TITLES.SHARED} ${getBadgeStyle(CHOOSE_CATEGORY_STYLE.TITLES, chooseCategoryState)}`}
          />
          {canCategorizeTransaction && <DownArrowIconTransparent className={`${CHOOSE_CATEGORY_STYLE.ARROWS.SHARED} ${getBadgeStyle(CHOOSE_CATEGORY_STYLE.ARROWS, chooseCategoryState)}`} /> }
        </>
      )}
    />
  );
};
